/**
 * Invoice email delivery tracking.
 *
 * Each successful send returns a `tracking_id` from the backend. We store
 * the full send history in localStorage per invoice, then poll the backend
 * `/track/status/{id}` to see whether the customer opened the email.
 */

const DELIVERY_KEY = 'maidly_invoice_deliveries';

const readAll = () => {
  try {
    return JSON.parse(localStorage.getItem(DELIVERY_KEY) || '{}');
  } catch (err) {
    console.warn('[deliveryTracking] malformed delivery cache reset', err);
    return {};
  }
};

const writeAll = (data) => {
  try {
    localStorage.setItem(DELIVERY_KEY, JSON.stringify(data));
  } catch (err) {
    console.warn('[deliveryTracking] failed to persist deliveries (quota?)', err);
  }
};

/** Append a new send record for an invoice. Called after a successful POST. */
export const recordDelivery = (invoiceId, record) => {
  if (!invoiceId || !record?.trackingId) return;
  const all = readAll();
  const list = Array.isArray(all[invoiceId]) ? all[invoiceId] : [];
  list.push({
    trackingId: record.trackingId,
    kind: record.kind || 'invoice',
    sentAt: record.sentAt || new Date().toISOString(),
    to: record.to || null,
    openedAt: null,
    opensCount: 0,
  });
  all[invoiceId] = list;
  writeAll(all);
};

/** All send records for an invoice, most-recent last. */
export const getDeliveries = (invoiceId) => {
  const all = readAll();
  return Array.isArray(all[invoiceId]) ? all[invoiceId] : [];
};

/** Latest delivery status for an invoice, or null if never sent. */
export const getLatestDelivery = (invoiceId) => {
  const list = getDeliveries(invoiceId);
  return list.length ? list[list.length - 1] : null;
};

/**
 * Refresh open status for every send that hasn't been marked opened yet.
 * Called on Invoices tab load. Silent on network errors.
 */
export const refreshOpenStatuses = async () => {
  const initialSnapshot = readAll();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const pending = [];
  Object.entries(initialSnapshot).forEach(([invoiceId, list]) => {
    (list || []).forEach((rec) => {
      if (rec?.trackingId && !rec.openedAt) {
        pending.push({ invoiceId, trackingId: rec.trackingId });
      }
    });
  });
  if (!pending.length) return { checked: 0, opened: 0, changed: [] };

  // Fetch statuses in parallel, then persist by tracking-id merge so we
  // don't overwrite records `recordDelivery` may have added concurrently.
  const updates = [];
  await Promise.all(
    pending.map(async ({ invoiceId, trackingId }) => {
      try {
        const res = await fetch(`${backendUrl}/api/invoices/track/status/${trackingId}`);
        if (!res.ok) return;
        const body = await res.json();
        if (body?.opened_at) {
          updates.push({
            invoiceId,
            trackingId,
            openedAt: body.opened_at,
            opensCount: Number(body.opens_count || 1),
          });
        }
      } catch (err) {
        // Offline / DNS failure — swallow but surface in dev console so
        // it's obvious when the tracking service is unreachable.
        console.warn('[deliveryTracking] status fetch failed', err);
      }
    }),
  );
  if (!updates.length) return { checked: pending.length, opened: 0, changed: [] };

  // Re-read at write-time and merge in place so concurrent recordDelivery
  // calls survive.
  const changed = new Set();
  const fresh = readAll();
  updates.forEach(({ invoiceId, trackingId, openedAt, opensCount }) => {
    const list = Array.isArray(fresh[invoiceId]) ? fresh[invoiceId] : [];
    const idx = list.findIndex((r) => r?.trackingId === trackingId);
    if (idx >= 0) {
      list[idx] = { ...list[idx], openedAt, opensCount };
      fresh[invoiceId] = list;
      changed.add(invoiceId);
    }
  });
  writeAll(fresh);
  if (changed.size && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('maidly:delivery-updated', {
      detail: { invoiceIds: Array.from(changed) },
    }));
  }
  return { checked: pending.length, opened: changed.size, changed: Array.from(changed) };
};

/**
 * Human-readable status label + color for the latest delivery of an invoice.
 * { label, color, tooltip }.
 */
export const getDeliveryBadge = (invoiceId) => {
  const latest = getLatestDelivery(invoiceId);
  if (!latest) return null;
  if (latest.openedAt) {
    return {
      label: 'Opened',
      color: '#059669',
      background: '#ecfdf5',
      tooltip: `Opened ${new Date(latest.openedAt).toLocaleString()}${latest.opensCount > 1 ? ` (${latest.opensCount} opens)` : ''}`,
    };
  }
  return {
    label: 'Sent',
    color: '#0369a1',
    background: '#e0f2fe',
    tooltip: `Sent ${new Date(latest.sentAt).toLocaleString()} — no open recorded yet`,
  };
};
